import { useEffect, useId, useRef, useState, type MouseEvent } from "react";
import { Link } from "react-router-dom";
import { BrandLogo } from "../../app/BrandLogo";
import { Icon } from "../../app/Icon";
import { CreateLink } from "./CreateLink";
import { useAuth } from "../auth/auth";

export function LandingNavigation({ showTestimonials, homeSections = false }: { showTestimonials: boolean; homeSections?: boolean }) {
  const { account, loading } = useAuth();
  const accountLabel = account ? "Your workspace" : "Sign in";
  const accountPath = account ? "/ai" : "/sign-in";
  const [open, setOpen] = useState(false);
  const dialogId = useId();
  const dialogRef = useRef<HTMLDialogElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const closeRef = useRef<HTMLButtonElement>(null);
  const sectionRef = useRef<string | null>(null);
  const sectionHref = (id: string) => `${homeSections ? "/" : ""}#${id}`;

  useEffect(() => {
    const dialog = dialogRef.current;
    if (!open || !dialog) return;
    const trigger = triggerRef.current;
    const previousOverflow = document.body.style.overflow;
    const previousRootOverflow = document.documentElement.style.overflow;
    dialog.showModal();
    document.body.style.overflow = "hidden";
    document.documentElement.style.overflow = "hidden";
    closeRef.current?.focus();

    const desktop = window.matchMedia("(min-width: 901px)");
    const onResize = () => { if (desktop.matches) setOpen(false); };
    desktop.addEventListener("change", onResize);

    return () => {
      desktop.removeEventListener("change", onResize);
      dialog.close();
      document.body.style.overflow = previousOverflow;
      document.documentElement.style.overflow = previousRootOverflow;
      if (sectionRef.current) {
        const id = sectionRef.current;
        sectionRef.current = null;
        window.location.hash = id;
        const section = document.getElementById(id);
        section?.scrollIntoView({ block: "start", behavior: "instant" });
        const heading = section?.querySelector("h2");
        heading?.setAttribute("tabindex", "-1");
        heading?.focus({ preventScroll: true });
      } else if (trigger?.isConnected) {
        trigger.focus({ preventScroll: true });
      }
    };
  }, [open]);

  function navigate(event: MouseEvent<HTMLElement>) {
    const link = (event.target as Element).closest("a");
    if (!link || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    const href = link.getAttribute("href");
    if (href?.startsWith("#")) {
      event.preventDefault();
      sectionRef.current = href.slice(1);
    }
    setOpen(false);
  }

  return <header className="landing-header">
    <Link className="landing-brand" to="/" aria-label="Stencil Studio home"><BrandLogo /></Link>
    <nav className="landing-desktop-nav" aria-label="Homepage navigation">
      <a href={sectionHref("how-it-works")}>How it works</a>
      <a href={sectionHref("results")}>Results</a>
      <a href={sectionHref("pricing")}>Pricing</a>
      <Link to="/blog">Blog</Link>
      <div className="landing-desktop-actions">{!loading && <Link to={accountPath}>{accountLabel}</Link>}<CreateLink label={account ? "Create stencil" : "Create free stencil"} header placement="header" /></div>
    </nav>
    <button className="landing-menu-trigger" ref={triggerRef} type="button" aria-expanded={open} aria-controls={dialogId} aria-haspopup="dialog" onClick={() => setOpen(true)}>
      <span>Menu</span><svg className="icon" aria-hidden="true" viewBox="0 0 24 24" fill="none"><path d="M4 6h16M4 12h16M4 18h16" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" /></svg>
    </button>
    <dialog ref={dialogRef} id={dialogId} className="landing-mobile-menu" aria-label="Navigation menu" onCancel={event => { event.preventDefault(); setOpen(false); }} onKeyDown={event => {
      if (event.key !== "Tab") return;
      const targets = Array.from(event.currentTarget.querySelectorAll<HTMLElement>('a[href], button:not([disabled])')).filter(element => element.getClientRects().length > 0);
      const first = targets[0];
      const last = targets[targets.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last?.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first?.focus();
      }
    }} onClick={event => {
      if (event.target !== event.currentTarget) return;
      const bounds = event.currentTarget.getBoundingClientRect();
      if (event.clientX < bounds.left || event.clientX > bounds.right || event.clientY < bounds.top || event.clientY > bounds.bottom) setOpen(false);
    }}>
      <div className="landing-mobile-menu__header">
        <Link className="landing-brand" to="/" aria-label="Stencil Studio home" onClick={() => setOpen(false)}><BrandLogo /></Link>
        <button ref={closeRef} type="button" className="landing-menu-close" aria-label="Close menu" onClick={() => setOpen(false)}><Icon name="close" /></button>
      </div>
      <nav aria-label="Mobile navigation" onClick={navigate}>
        <a href={sectionHref("how-it-works")}>How it works<Icon name="arrow-right" /></a>
        <a href={sectionHref("results")}>See the results<Icon name="arrow-right" /></a>
        {showTestimonials && <a href={sectionHref("testimonials")}>Artist reviews<Icon name="arrow-right" /></a>}
        <a href={sectionHref("pricing")}>Pricing<Icon name="arrow-right" /></a>
        <Link to="/blog">Blog<Icon name="arrow-right" /></Link>
        {!loading && <Link to={accountPath}>{accountLabel}<Icon name="arrow-right" /></Link>}
      </nav>
      <div className="landing-mobile-menu__action" onClick={navigate}><CreateLink label={account ? "Create stencil" : "Create my free stencil"} placement="mobile_menu" />{!account && !loading && <p>2 free generations · No card required</p>}</div>
    </dialog>
  </header>;
}
